import { useEffect, useState } from 'react'
import { LayoutDashboard, AlertTriangle, Search, Brain, ShieldCheck, Key, ClipboardCheck,
  ShieldAlert, Wrench, Award, UserCheck, HardHat, Shield, CheckSquare, BarChart3,
  LineChart, Clock, RotateCcw, Save } from 'lucide-react'
import api from '../../services/api'
import { invalidateNavCache } from '../../hooks/useNavPermissions'

const NAV_META: Record<string, { label: string; icon: any; group: string }> = {
  'dashboard':         { label: 'Dashboard',          icon: LayoutDashboard, group: '' },
  'incidents':         { label: 'Incidents',           icon: AlertTriangle,   group: 'Safety' },
  'investigations':    { label: 'Investigations',      icon: Search,          group: 'Safety' },
  'psychosocial':      { label: 'Psychosocial',        icon: Brain,           group: 'Safety' },
  'lone-worker':       { label: 'Lone Worker',         icon: ShieldCheck,     group: 'Safety' },
  'permits':           { label: 'Permits to Work',     icon: Key,             group: 'Operations' },
  'audits':            { label: 'Audits',              icon: ClipboardCheck,  group: 'Operations' },
  'risk-register':     { label: 'Risk Register',       icon: ShieldAlert,     group: 'Operations' },
  'assets':            { label: 'Assets',              icon: Wrench,          group: 'Operations' },
  'competencies':      { label: 'Competencies',        icon: Award,           group: 'Workforce' },
  'workforce':         { label: 'Workforce',           icon: UserCheck,       group: 'Workforce' },
  'contractors':       { label: 'Contractors',         icon: HardHat,         group: 'Workforce' },
  'workers-comp':      { label: 'Workers Comp',        icon: Shield,          group: 'Compliance' },
  'corrective-actions':{ label: 'Corrective Actions',  icon: CheckSquare,     group: 'Compliance' },
  'metrics':           { label: 'Metrics',             icon: BarChart3,       group: 'Reporting' },
  'analytics':         { label: 'Analytics',           icon: LineChart,       group: 'Reporting' },
  'scheduled-reports': { label: 'Scheduled Reports',   icon: Clock,           group: 'Reporting' },
}

const ROLE_LABELS: Record<string, string> = {
  // Tenant staff
  read_only:             'Read Only',
  field_worker:          'Field Worker',
  supervisor:            'Supervisor',
  ops_safety:            'OHS Officer',
  corp_safety:           'Corp Safety',
  // Contractor company hierarchy
  contractor_worker:     'Contractor Worker',
  contractor_standard:   'Contractor (legacy)',
  contractor_supervisor: 'Contractor Supervisor',
  contractor_safety:     'Contractor Safety Officer',
  contractor_admin:      'Contractor Admin',
}

const ROLE_GROUPS: { label: string; roles: string[] }[] = [
  { label: 'Tenant Staff', roles: ['read_only', 'field_worker', 'supervisor', 'ops_safety', 'corp_safety'] },
  { label: 'Contractor Company', roles: ['contractor_worker', 'contractor_supervisor', 'contractor_safety', 'contractor_admin'] },
]

const GROUPS = ['', 'Safety', 'Operations', 'Workforce', 'Compliance', 'Reporting']

type Config = Record<string, { defaults: string[]; effective: string[]; customised: boolean }>

export default function RoleNavSettings() {
  const [allKeys, setAllKeys] = useState<string[]>([])
  const [config, setConfig] = useState<Config>({})
  const [loading, setLoading] = useState(true)
  const [dirty, setDirty] = useState<Set<string>>(new Set())
  const [saving, setSaving] = useState<string | null>(null)
  const [toast, setToast] = useState('')

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 3000) }

  const load = async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/role-nav/config')
      setAllKeys(data.all_keys)
      setConfig(data.config)
    } catch {}
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  const toggle = (role: string, key: string) => {
    setConfig(prev => {
      const current = [...(prev[role]?.effective || [])]
      const idx = current.indexOf(key)
      if (idx >= 0) current.splice(idx, 1)
      else current.push(key)
      return { ...prev, [role]: { ...prev[role], effective: current, customised: true } }
    })
    setDirty(prev => new Set([...prev, role]))
  }

  const save = async (role: string) => {
    setSaving(role)
    try {
      await api.put(`/role-nav/config/${role}`, { allowed_keys: config[role].effective })
      invalidateNavCache()
      setDirty(prev => { const n = new Set(prev); n.delete(role); return n })
      showToast(`Saved permissions for ${ROLE_LABELS[role] || role}`)
    } catch { showToast('Save failed') }
    setSaving(null)
  }

  const reset = async (role: string) => {
    if (!confirm(`Reset ${ROLE_LABELS[role] || role} to defaults?`)) return
    setSaving(role)
    try {
      const { data } = await api.delete(`/role-nav/config/${role}`)
      invalidateNavCache()
      setConfig(prev => ({ ...prev, [role]: { ...prev[role], effective: data.defaults, customised: false } }))
      setDirty(prev => { const n = new Set(prev); n.delete(role); return n })
      showToast('Reset to defaults')
    } catch { showToast('Reset failed') }
    setSaving(null)
  }

  const RoleTable = ({ group }: { group: { label: string; roles: string[] } }) => (
    <div style={{ marginBottom: 32 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10, paddingLeft: 2 }}>
        {group.label}
      </div>
      <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 12 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              <th style={{ textAlign: 'left', padding: '10px 14px', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb', minWidth: 170 }}>
                Nav item
              </th>
              {group.roles.map(role => (
                <th key={role} style={{ textAlign: 'center', padding: '10px 12px', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb', minWidth: 110, whiteSpace: 'nowrap' }}>
                  <div style={{ fontSize: 12 }}>{ROLE_LABELS[role]}</div>
                  {config[role]?.customised && (
                    <div style={{ fontSize: 10, color: '#6366f1', fontWeight: 500 }}>customised</div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {GROUPS.map(navGroup => {
              const groupKeys = allKeys.filter(k => NAV_META[k]?.group === navGroup)
              if (!groupKeys.length) return null
              return [
                navGroup && (
                  <tr key={`ng-${navGroup}`}>
                    <td colSpan={group.roles.length + 1} style={{ padding: '10px 14px 3px', fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', background: '#fafafa' }}>
                      {navGroup}
                    </td>
                  </tr>
                ),
                ...groupKeys.map(key => {
                  const meta = NAV_META[key]
                  const Icon = meta?.icon
                  return (
                    <tr key={key} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px 14px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                          {Icon && <Icon size={13} color="#9ca3af" />}
                          <span style={{ color: '#111827', fontWeight: 500 }}>{meta?.label || key}</span>
                        </div>
                      </td>
                      {group.roles.map(role => {
                        const enabled = config[role]?.effective?.includes(key) ?? false
                        const isLocked = role === 'tenant_admin'
                        return (
                          <td key={role} style={{ textAlign: 'center', padding: '8px 12px' }}>
                            <button
                              onClick={() => !isLocked && toggle(role, key)}
                              disabled={isLocked}
                              style={{
                                width: 22, height: 22, borderRadius: 6, border: 'none', cursor: isLocked ? 'default' : 'pointer',
                                background: enabled ? '#6366f1' : '#e5e7eb',
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                opacity: isLocked ? 0.4 : 1,
                                transition: 'background 150ms',
                              }}
                              title={isLocked ? 'Always has full access' : (enabled ? 'Click to hide' : 'Click to show')}
                            >
                              {enabled && <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                            </button>
                          </td>
                        )
                      })}
                    </tr>
                  )
                })
              ]
            })}
          </tbody>
        </table>
      </div>
      {/* Per-role save / reset for this group */}
      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        {group.roles.filter(r => r !== 'tenant_admin').map(role => {
          const isDirty = dirty.has(role)
          const isSaving = saving === role
          if (!isDirty && !config[role]?.customised) return null
          return (
            <div key={role} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, border: `1px solid ${isDirty ? '#6366f1' : '#e5e7eb'}`, background: isDirty ? '#eef2ff' : 'white' }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: isDirty ? '#4338ca' : '#6b7280' }}>{ROLE_LABELS[role]}</span>
              {isDirty && (
                <button onClick={() => save(role)} disabled={!!isSaving}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 6, background: '#6366f1', color: 'white', border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: isSaving ? 0.6 : 1 }}>
                  <Save size={11} /> {isSaving ? 'Saving…' : 'Save'}
                </button>
              )}
              {config[role]?.customised && (
                <button onClick={() => reset(role)} disabled={!!isSaving}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 7px', borderRadius: 6, background: 'white', color: '#9ca3af', border: '1px solid #e5e7eb', fontSize: 12, cursor: 'pointer' }}
                  title="Reset to defaults">
                  <RotateCcw size={11} />
                </button>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '28px 24px' }}>
      {toast && (
        <div style={{ position: 'fixed', top: 20, right: 20, background: '#1e293b', color: 'white', padding: '10px 18px', borderRadius: 10, fontSize: 13, zIndex: 9999 }}>
          {toast}
        </div>
      )}

      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 4px' }}>Role Navigation Permissions</h1>
        <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>
          Control which sidebar sections each role can see. Tenant staff and contractor company employees are configured separately. Backend API enforcement is unchanged.
        </p>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px 0', color: '#9ca3af' }}>Loading…</div>
      ) : (
        <>
          {ROLE_GROUPS.map(group => (
            <RoleTable key={group.label} group={group} />
          ))}
          {/* empty fragment closing tags below — keep existing JSX structure */}
          <div style={{ display: 'none' }}>{/* placeholder to maintain structure */}</div>
        </>
      )}
    </div>
  )
}
